/**
 * MenuCategoryTabs Component
 * 
 * Displays category navigation with icons and smooth scrolling
 */

import React, { useEffect, useRef } from 'react';
import { useMenuTheme } from '@/hooks/useMenuTheme';
import type { Category } from '@/hooks/useCategories';
import { cn } from '@/lib/utils';
import { FontWeight } from '@/types/menuCustomization';

interface MenuCategoryTabsProps {
  categories: Category[];
  activeCategory: string | null;
  onCategoryClick: (categoryId: string) => void;
  className?: string;
}

export function MenuCategoryTabs({
  categories,
  activeCategory,
  onCategoryClick,
  className = '',
}: MenuCategoryTabsProps) {
  const { customization, branch } = useMenuTheme();
  const { categoryTabs } = customization.components;
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll active category into view
  useEffect(() => {
    if (activeCategory && scrollContainerRef.current) {
      const activeElement = scrollContainerRef.current.querySelector(
        `[data-category-id="${activeCategory}"]`
      );
      if (activeElement) {
        activeElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      }
    }
  }, [activeCategory]);

  const handleCategoryClick = (categoryId: string) => {
    onCategoryClick(categoryId);
    
    if (categoryTabs.scrollToCategory) {
      const element = document.getElementById(`category-section-${categoryId}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  const getIconSize = () => {
    switch (categoryTabs.size) {
      case 'sm': return 'w-14 h-14 sm:w-16 sm:h-16';
      case 'lg': return 'w-20 h-20 sm:w-24 sm:h-24';
      default: return 'w-16 h-16 sm:w-20 sm:h-20';
    }
  };

  const getTextSize = () => {
    switch (categoryTabs.size) {
      case 'sm': return 'text-xs sm:text-sm';
      case 'lg': return 'text-sm sm:text-base';
      default: return 'text-xs sm:text-sm';
    }
  };

  const getFontWeightClass = (weight: FontWeight) => {
    switch (weight) {
      case 'light': return 'font-light';
      case 'normal': return 'font-normal';
      case 'medium': return 'font-medium';
      case 'semibold': return 'font-semibold';
      case 'bold': return 'font-bold';
      case 'extrabold': return 'font-extrabold';
      default: return 'font-semibold';
    }
  };

  return (
    <div
      className={`${categoryTabs.sticky ? 'sticky top-0' : ''} backdrop-blur-sm z-10 py-3 sm:py-4 -mx-4 px-4 border-b bg-white/95 shadow-sm ${className}`}
    >
      <div className="flex justify-between items-center">
        <div
          ref={scrollContainerRef}
          className="flex gap-x-4 sm:gap-x-6 overflow-x-auto pb-2 no-scrollbar scroll-smooth"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {categories.map((cat) => (
            <div
              key={cat.id}
              data-category-id={cat.id}
              onClick={() => handleCategoryClick(cat.id)}
              className={`flex flex-col items-center space-y-2 flex-shrink-0 text-center cursor-pointer transition-colors ${
                activeCategory === cat.id ? '' : 'text-gray-500'
              }`}
              style={{ color: activeCategory === cat.id ? branch.primary_color : undefined }}
            >
              {categoryTabs.showIcons && (
                <div
                  className={`${getIconSize()} p-1 flex items-center justify-center bg-red-50 transition-all ${
                    categoryTabs.iconStyle === 'circle' ? 'rounded-full' :
                    categoryTabs.iconStyle === 'square' ? 'rounded-none' :
                    'rounded-lg'
                  }`}
                  style={{
                    borderColor: activeCategory === cat.id ? branch.primary_color : 'transparent',
                    borderWidth: activeCategory === cat.id ? '2px' : '0px',
                    borderStyle: 'solid',
                  }}
                >
                  <img
                    src={cat.image_url || 'https://menux.app/wp-content/uploads/2025/06/Group-1-300x300.png'}
                    alt={cat.name}
                    className={`w-full h-full object-contain ${
                      categoryTabs.iconStyle === 'circle' ? 'rounded-full' : ''
                    }`}
                  />
                </div>
              )}
              <span className={cn(
                getTextSize(), 
                getFontWeightClass(customization.typography.headingWeight),
                "max-w-[100px] text-center leading-tight"
              )}>
                {cat.name}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
